import { getRequiredElement, escapeHtml } from './dom';
import { showToast } from './toast';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const VALID_EXTENSIONS = ['lrc', 'txt', 'text'];

/**
 * Validates and reads an uploaded or dropped LRC file into the input textarea.
 *
 * @param file File object from input picker or DataTransfer.
 * @param onFileLoaded Callback invoked once text is loaded successfully.
 */
export function handleFile(
  file: File | null | undefined,
  onFileLoaded: (content: string, filename: string) => void
): void {
  if (!file) return;

  if (file.size > MAX_FILE_SIZE) {
    showToast('File too large (max 5MB for LRC lyrics).');
    return;
  }

  const ext = (file.name || '').split('.').pop()?.toLowerCase() || '';
  if (!VALID_EXTENSIONS.includes(ext) && file.type && !file.type.startsWith('text/')) {
    showToast('Please select a valid .lrc or .txt lyric file.');
    return;
  }

  const reader = new FileReader();

  reader.onload = (evt) => {
    const content = typeof evt.target?.result === 'string' ? evt.target.result : '';
    onFileLoaded(content, file.name);
    showToast(`✓ Loaded "${escapeHtml(file.name)}"`);
  };

  reader.onerror = () => {
    showToast('Failed to read file.');
  };

  reader.readAsText(file);
}

/**
 * Initializes drag-and-drop and file input picker listeners.
 */
export function initFileHandlers(
  onFileLoaded: (content: string, filename: string) => void
): void {
  const filePicker = getRequiredElement<HTMLInputElement>('filePicker');
  const card = (document.getElementById('studioCard') || document.getElementById('inputCard')) as HTMLDivElement;
  if (!card) {
    throw new Error('Critical DOM element not found: #studioCard or #inputCard');
  }

  filePicker.addEventListener('change', () => {
    if (filePicker.files && filePicker.files[0]) {
      handleFile(filePicker.files[0], onFileLoaded);
    }
    filePicker.value = '';
  });

  // Prevent default browser file navigation on accidental drops outside
  window.addEventListener('dragover', (e) => e.preventDefault());
  window.addEventListener('drop', (e) => e.preventDefault());

  let dragCounter = 0;

  card.addEventListener('dragenter', (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter++;
    card.classList.add('drag-active');
  });

  card.addEventListener('dragover', (e) => {
    e.preventDefault();
    e.stopPropagation();
  });

  card.addEventListener('dragleave', (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter--;
    if (dragCounter <= 0) {
      dragCounter = 0;
      card.classList.remove('drag-active');
    }
  });

  card.addEventListener('drop', (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter = 0;
    card.classList.remove('drag-active');

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleFile(files[0], onFileLoaded);
    }
  });
}
